<template>
  <v-dialog
    :value="value"
    @input="onDialogInput"
    :width="400"
    content-class="tw-m-0"
  >
    <v-card>
      <!-- Main sign-in screen -->
      <template v-if="step === 'select'">
        <v-card-title>{{ $t("common.signIn") }}</v-card-title>
        <v-card-text class="tw-flex tw-flex-col tw-items-center">
          <div class="tw-mb-4 tw-flex tw-w-full tw-flex-col tw-gap-y-2">
            <v-btn
              block
              @click="signIn(calendarTypes.GOOGLE)"
              class="tw-bg-white"
            >
              <div class="tw-flex tw-w-full tw-items-center tw-gap-2">
                <v-img
                  class="tw-flex-initial"
                  width="20"
                  height="20"
                  src="@/assets/google_logo.svg"
                />
                <v-spacer />
                {{ $t("signIn.continueWith", { provider: "Google" }) }}
                <v-spacer />
              </div>
            </v-btn>
            <v-btn
              block
              @click="signIn(calendarTypes.OUTLOOK)"
              class="tw-bg-white"
            >
              <div class="tw-flex tw-w-full tw-items-center tw-gap-2">
                <v-img
                  class="tw-flex-initial"
                  width="20"
                  height="20"
                  src="@/assets/outlook_logo.svg"
                />
                <v-spacer />
                {{ $t("signIn.continueWith", { provider: "Outlook" }) }}
                <v-spacer />
              </div>
            </v-btn>

            <div class="tw-my-2 tw-flex tw-items-center tw-gap-3">
              <v-divider />
              <span class="tw-text-gray-500 tw-text-xs">{{ $t("signIn.or") }}</span>
              <v-divider />
            </div>

            <div>
              <div class="tw-mb-1 tw-text-sm tw-font-medium">
                {{ $t("signIn.emailAddress") }}
              </div>
              <v-text-field
                v-model="email"
                class="tw-mb-2"
                :placeholder="$t('signIn.emailPlaceholder')"
                type="email"
                solo
                hide-details="auto"
                :error-messages="emailError"
                @keydown.enter="submitEmail"
              />
              <v-btn
                block
                color="primary"
                :loading="sending"
                :disabled="sending"
                @click="submitEmail"
              >
                {{ $t("signIn.continueWith", { provider: "Email" }) }}
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </template>

      <!-- Onboarding: name entry for new users -->
      <template v-else-if="step === 'onboarding'">
        <v-card-title class="tw-flex tw-items-center">
          <v-btn icon small @click="step = 'select'" class="tw-mr-1">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          {{ $t("signIn.whatsYourName") }}
        </v-card-title>
        <v-card-text>
          <p class="tw-text-gray-600 tw-mb-4 tw-text-sm">
            {{ $t("signIn.needDetails") }}
          </p>
          <div class="tw-mb-1 tw-text-sm tw-font-medium">
            {{ $t("signIn.firstName") }}
          </div>
          <v-text-field
            v-model="firstName"
            :placeholder="$t('signIn.firstName')"
            solo
            hide-details="auto"
            autofocus
            @keydown.enter="$refs.lastNameField && $refs.lastNameField.focus()"
            class="tw-mb-3"
          />
          <div class="tw-mb-1 tw-text-sm tw-font-medium">
            {{ $t("signIn.lastName") }}
          </div>
          <v-text-field
            ref="lastNameField"
            v-model="lastName"
            :placeholder="$t('signIn.lastNameOptional')"
            solo
            hide-details="auto"
            @keydown.enter="submitOnboarding"
            class="tw-mb-3"
          />
          <div class="tw-mb-1 tw-text-sm tw-font-medium">
            {{ $t("signIn.email") }}
          </div>
          <v-text-field
            :value="email"
            :placeholder="$t('signIn.emailDots')"
            solo
            hide-details="auto"
            disabled
            class="tw-mb-3"
          />
          <v-btn
            block
            color="primary"
            :loading="sending"
            :disabled="!firstName.trim() || sending"
            @click="submitOnboarding"
          >
            {{ $t("common.continue") }}
          </v-btn>
        </v-card-text>
      </template>

      <!-- OTP code input -->
      <template v-else-if="step === 'otp'">
        <v-card-title class="tw-flex tw-items-center">
          <v-btn
            icon
            small
            @click="step = isNewUser ? 'onboarding' : 'select'"
            class="tw-mr-1"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          {{ $t("signIn.enterCode") }}
        </v-card-title>
        <v-card-text>
          <p class="tw-text-gray-600 tw-mb-4 tw-text-sm">
            {{ $t("signIn.codeSentTo") }}
            <strong>{{ email }}</strong>
          </p>
          <div class="tw-mb-1 tw-text-sm tw-font-medium">
            {{ $t("signIn.verificationCode") }}
          </div>
          <v-text-field
            v-model="otpCode"
            :placeholder="$t('signIn.codePlaceholder')"
            solo
            hide-details="auto"
            maxlength="6"
            :error-messages="otpError"
            @keydown.enter="verifyOtp"
            autofocus
            class="tw-mb-2"
          />
          <v-btn
            block
            color="primary"
            :loading="verifying"
            :disabled="otpCode.length !== 6 || verifying"
            @click="verifyOtp"
          >
            {{ $t("signIn.verify") }}
          </v-btn>
          <div class="tw-mt-3 tw-text-center">
            <v-btn
              text
              x-small
              :disabled="resendCooldown > 0"
              @click="resendOtp"
            >
              {{
                resendCooldown > 0
                  ? $t("signIn.resendIn", { s: resendCooldown })
                  : $t("signIn.resend")
              }}
            </v-btn>
          </div>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { calendarTypes } from "@/constants"
import { post } from "@/utils"

export default {
  name: "SignInDialog",
  props: {
    value: { type: Boolean, required: true },
  },
  data() {
    return {
      calendarTypes,
      step: "select",
      email: "",
      firstName: "",
      lastName: "",
      otpCode: "",
      emailError: "",
      otpError: "",
      sending: false,
      verifying: false,
      isNewUser: false,
      resendCooldown: 0,
      resendTimer: null,
    }
  },
  methods: {
    signIn(provider) {
      this.$emit("signIn", provider)
    },
    validateEmail() {
      const email = this.email.trim()
      if (!email) {
        this.emailError = this.$t("signIn.errors.enterEmail")
        return false
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.emailError = this.$t("signIn.errors.invalidEmail")
        return false
      }
      if (email.includes("+")) {
        this.emailError = this.$t("signIn.errors.noAliases")
        return false
      }
      return true
    },
    async submitEmail() {
      if (this.sending) return
      this.emailError = ""
      if (!this.validateEmail()) return
      this.sending = true
      try {
        const res = await post("/auth/otp/check-email", { email: this.email })
        this.isNewUser = res.isNewUser
        if (this.isNewUser) {
          this.step = "onboarding"
        } else {
          await this.sendOtpEmail()
          this.step = "otp"
          this.otpCode = ""
          this.otpError = ""
        }
      } catch (err) {
        this.emailError = this.$t("signIn.errors.generic")
      } finally {
        this.sending = false
      }
    },
    async submitOnboarding() {
      if (!this.firstName.trim() || this.sending) return
      this.sending = true
      try {
        await this.sendOtpEmail()
        this.step = "otp"
        this.otpCode = ""
        this.otpError = ""
      } catch (err) {
        this.otpError = this.$t("signIn.errors.sendFailed")
      } finally {
        this.sending = false
      }
    },
    async sendOtpEmail() {
      await post("/auth/otp/send", { email: this.email })
      this.startResendCooldown()
    },
    async resendOtp() {
      if (this.sending || this.resendCooldown > 0) return
      this.sending = true
      try {
        await this.sendOtpEmail()
        this.otpCode = ""
        this.otpError = ""
      } catch (err) {
        this.otpError = this.$t("signIn.errors.resendFailed")
      } finally {
        this.sending = false
      }
    },
    async verifyOtp() {
      if (this.otpCode.length !== 6 || this.verifying) return
      this.otpError = ""
      this.verifying = true
      try {
        const body = {
          email: this.email,
          code: this.otpCode,
          timezoneOffset: new Date().getTimezoneOffset(),
        }
        if (this.isNewUser) {
          body.firstName = this.firstName.trim()
          body.lastName = this.lastName.trim()
        }
        const user = await post("/auth/otp/verify", body)
        this.$emit("emailSignIn", user)
        this.reset()
        this.$emit("input", false)
      } catch (err) {
        const errorCode = err?.parsed?.error
        if (errorCode === "otp-expired") {
          this.otpError = this.$t("signIn.errors.expired")
        } else if (errorCode === "otp-too-many-attempts") {
          this.otpError = this.$t("signIn.errors.tooMany")
        } else {
          this.otpError = this.$t("signIn.errors.invalidCode")
        }
      } finally {
        this.verifying = false
      }
    },
    startResendCooldown() {
      this.resendCooldown = 30
      if (this.resendTimer) clearInterval(this.resendTimer)
      this.resendTimer = setInterval(() => {
        this.resendCooldown--
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer)
          this.resendTimer = null
        }
      }, 1000)
    },
    reset() {
      this.step = "select"
      this.email = ""
      this.firstName = ""
      this.lastName = ""
      this.otpCode = ""
      this.emailError = ""
      this.otpError = ""
      this.sending = false
      this.verifying = false
      this.isNewUser = false
      this.resendCooldown = 0
      if (this.resendTimer) clearInterval(this.resendTimer)
    },
    onDialogInput(e) {
      this.$emit("input", e)
      if (!e) this.reset()
    },
  },
  beforeDestroy() {
    if (this.resendTimer) clearInterval(this.resendTimer)
  },
}
</script>
